import CampaignIcon from 'assets/icons/mono/CampaignIcon';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toggleAddGSTModalVisibility } from 'slices/ui/reducer';
import { AppDispatch } from 'store';
import { Feature, Features } from 'types/dashboard';

interface Props {
    features: Features | null;
    loading: boolean;
}

const FeaturesIntroducer: React.FC<Props> = ({ features, loading }) => {

    const featureList = useMemo(() => {
        if (!features) return [];

        return Object.entries(features).map(([key, value]) => ({
            key,
            ...value,
        })).sort((f: Feature, s: Feature) => f.sort_order - s.sort_order);
    }, [features]);

    const dispatch:AppDispatch = useDispatch();

    const completedCount = featureList.filter(f => f.status).length;
    const totalCount = featureList.length;

    // if (!loading && completedCount === totalCount) return null;

    return (
        <div className="dashboard-section">
            <div className="dashboard-section-card p-4">

                {/* Header */}
                <div className="dashboard-section-header mb-3">
                    {
                        (loading) ? <Skeleton height={20} width={250} /> :
                            (<h5 className="fw-semibold mb-0">
                                Complete these tasks to get your first free campaign
                            </h5>)
                    }

                </div>
                <div className="mb-3">
                    <div className="d-flex align-items-center gap-3 positionRelative">
                        {
                            (loading) ? (
                                Array.from({ length: 3 }).map(() => <Skeleton width={150} className='feature-pill' />)
                            ) : (
                                featureList.map((_, i) => {
                                    return (
                                        <div className={`feature-pill w-100 ${i < completedCount ? 'completed' : ''}`}></div>
                                    )
                                })
                            )
                        }
                        {
                            !(loading) &&
                            <button className='btn btn-icon btn-purple-gradient p-3'><i className='bx bxs-megaphone'></i></button>
                        }
                    </div>
                    {
                        (loading) ? <Skeleton height={15} width={30} /> : <span className="completed-count">{completedCount}/{totalCount}</span>
                    }

                </div>

                {/* Feature Cards */}
                <div className="row g-3">
                    {
                        (loading) ? (
                            Array.from({ length: 2 }).map(() => <div className='col-md-6 h-100'><Skeleton className='feature-card ' height={100} width={'100%'} /></div>)
                        ) : (
                            featureList.map((feature) => (
                                <div key={feature.key} className="col-md-6">
                                    <div
                                        className={`feature-card h-100`}
                                    >
                                        <div className={`icon-badge`}>
                                            {
                                                feature.status === true ? (
                                                    <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect width="62" height="62" rx="8" fill="#EBF8F3" />
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M49.3346 31.0013C49.3346 41.1265 41.1265 49.3346 31.0013 49.3346C20.8761 49.3346 12.668 41.1265 12.668 31.0013C12.668 20.8761 20.8761 12.668 31.0013 12.668C41.1265 12.668 49.3346 20.8761 49.3346 31.0013ZM38.3902 25.4457C38.9272 25.9827 38.9272 26.8533 38.3902 27.3902L29.2236 36.5569C28.6866 37.0939 27.816 37.0939 27.279 36.5569L23.6124 32.8902C23.0754 32.3533 23.0754 31.4827 23.6124 30.9457C24.1493 30.4087 25.0199 30.4087 25.5569 30.9457L28.2513 33.6401L32.3485 29.5429L36.4457 25.4457C36.9827 24.9087 37.8533 24.9087 38.3902 25.4457Z" fill="#0BAC68" />
                                                    </svg>
                                                ) : (
                                                    <>
                                                        {
                                                            feature.key === 'collabs' &&
                                                            <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <rect width="62" height="62" rx="8" fill="#FFE6D4" />
                                                                <path d="M17.668 31.0013C17.668 24.7159 17.668 21.5732 19.6206 19.6206C21.5732 17.668 24.7159 17.668 31.0013 17.668C37.2867 17.668 40.4294 17.668 42.382 19.6206C44.3346 21.5732 44.3346 24.7159 44.3346 31.0013C44.3346 37.2867 44.3346 40.4294 42.382 42.382C40.4294 44.3346 37.2867 44.3346 31.0013 44.3346C24.7159 44.3346 21.5732 44.3346 19.6206 42.382C17.668 40.4294 17.668 37.2867 17.668 31.0013Z" stroke="#F47842" strokeWidth="2" />
                                                                <path d="M43.6654 25.668H18.332" stroke="#F47842" strokeWidth="2" strokeLinecap="round" />
                                                                <path d="M28.9987 18.332L24.332 25.6654" stroke="#F47842" strokeWidth="2" strokeLinecap="round" />
                                                                <path d="M37.6667 18.332L33 25.6654" stroke="#F47842" strokeWidth="2" strokeLinecap="round" />
                                                                <path d="M23.668 33.668H34.3346" stroke="#F47842" strokeWidth="2" strokeLinecap="round" />
                                                                <path d="M23.668 38.332H31.0013" stroke="#F47842" strokeWidth="2" strokeLinecap="round" />
                                                            </svg>

                                                        }
                                                        {
                                                            feature.key === 'gst' &&
                                                            <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <rect width="62" height="62" rx="8" fill="#DEFFFD" />
                                                                <path d="M23 25.668H28.3333" stroke="#3CC0F8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M42.7778 27H39.3077C36.9286 27 35 28.7909 35 31C35 33.2091 36.9286 35 39.3077 35H42.7778C42.8889 35 42.9444 35 42.9913 34.9971C43.7104 34.9534 44.2831 34.4215 44.3303 33.7539C44.3333 33.7103 44.3333 33.6587 44.3333 33.5556V28.4444C44.3333 28.3413 44.3333 28.2897 44.3303 28.2461C44.2831 27.5785 43.7104 27.0466 42.9913 27.0029C42.9444 27 42.8889 27 42.7778 27Z" stroke="#3CC0F8" strokeWidth="2" />
                                                                <path d="M42.9547 26.9987C42.851 24.5023 42.5168 22.9717 41.4392 21.8941C39.8771 20.332 37.363 20.332 32.3346 20.332L28.3346 20.332C23.3063 20.332 20.7922 20.332 19.2301 21.8941C17.668 23.4562 17.668 25.9704 17.668 30.9987C17.668 36.027 17.668 38.5412 19.2301 40.1033C20.7922 41.6654 23.3063 41.6654 28.3346 41.6654H32.3346C37.363 41.6654 39.8771 41.6654 41.4392 40.1033C42.5168 39.0257 42.851 37.4951 42.9547 34.9987" stroke="#3CC0F8" strokeWidth="2" />
                                                                <path d="M38.9883 31H39.0003" stroke="#3CC0F8" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>

                                                        }
                                                        {
                                                            feature.key === 'competitor' &&
                                                            <svg
                                                                width="62"
                                                                height="62"
                                                                viewBox="0 0 62 62"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                {/* Background */}
                                                                <rect width="62" height="62" rx="8" fill="#E6F3FA" />

                                                                {/* Centered Icon */}
                                                                <g transform="translate(17,17)">
                                                                    <svg
                                                                        width="28"
                                                                        height="28"
                                                                        viewBox="0 0 20 20"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M15 6.66797C15 9.42939 12.7614 11.668 10 11.668C7.23858 11.668 5 9.42939 5 6.66797C5 3.90654 7.23858 1.66797 10 1.66797C12.7614 1.66797 15 3.90654 15 6.66797Z"
                                                                            stroke="#0069AD"
                                                                            strokeWidth="1.25"
                                                                        />
                                                                        <path
                                                                            d="M5.41797 8.49219C3.26143 9.04724 1.66797 11.0049 1.66797 13.3347C1.66797 16.0961 3.90654 18.3347 6.66797 18.3347C9.42939 18.3347 11.668 16.0961 11.668 13.3347C11.668 12.7058 11.5519 12.104 11.3399 11.5496"
                                                                            stroke="#0069AD"
                                                                            strokeWidth="1.25"
                                                                        />
                                                                        <path
                                                                            d="M10 17.0615C10.8846 17.8532 12.0527 18.3347 13.3333 18.3347C16.0948 18.3347 18.3333 16.0961 18.3333 13.3347C18.3333 11.0049 16.7399 9.04724 14.5833 8.49219"
                                                                            stroke="#0069AD"
                                                                            strokeWidth="1.25"
                                                                        />
                                                                    </svg>
                                                                </g>
                                                            </svg>

                                                        }
                                                    </>
                                                )
                                            }
                                        </div>
                                        <div>
                                            <h6 className="feature-title">
                                                {feature.title}
                                            </h6>
                                            <p className="feature-description">
                                                {feature.description}
                                            </p>

                                            {(!feature.status) && feature.cta && (
                                                <>
                                                    {
                                                        feature.key === 'gst' ? (<button className="btn btn-primary btn-sm feature-cta" onClick={() => dispatch(toggleAddGSTModalVisibility(true))}>{feature.cta.text}</button>) :
                                                            <Link to={{ pathname: feature.cta.link }} className="btn btn-primary btn-sm feature-cta">
                                                                {feature.cta.text}
                                                            </Link>
                                                    }
                                                </>

                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )
                    }
                </div>

            </div>
        </div>
    );
};

export default FeaturesIntroducer;
